/**
 * P-256 (secp256r1) Key Generation and Signing
 *
 * Generates P-256 keypairs for session signing.
 * Private keys are stored encrypted in IndexedDB.
 *
 * The public key (x, y coordinates) is exported to add as a signer on-chain.
 * The private key never leaves the iframe.
 */

export interface P256PublicKey {
  x: string; // base64-encoded 32-byte x coordinate
  y: string; // base64-encoded 32-byte y coordinate
}

export interface P256Keypair {
  privateKey: Uint8Array; // Raw private key (32 bytes)
  publicKey: P256PublicKey;
}

/**
 * Generate a new P-256 keypair.
 *
 * @returns Keypair with raw private key and base64-encoded public coordinates
 */
export async function generateP256Keypair(): Promise<P256Keypair> {
  // Generate P-256 keypair using Web Crypto API
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDSA',
      namedCurve: 'P-256',
    },
    true, // Extractable (so we can export private key for storage)
    ['sign', 'verify']
  );

  // Export private key as raw bytes
  const privateKeyBuffer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
  const privateKeyRaw = extractRawPrivateKey(new Uint8Array(privateKeyBuffer));

  // Export public key to get x,y coordinates
  const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const { x, y } = extractPublicKeyCoordinates(new Uint8Array(publicKeyBuffer));

  return {
    privateKey: privateKeyRaw,
    publicKey: {
      x: arrayToBase64(x),
      y: arrayToBase64(y),
    },
  };
}

/**
 * Import a P-256 private key for signing.
 *
 * @param privateKey - Raw 32-byte private key
 * @returns CryptoKey for signing
 */
export async function importPrivateKey(privateKey: Uint8Array): Promise<CryptoKey> {
  console.log('[P256] importPrivateKey: privateKey length =', privateKey.length);

  if (privateKey.length !== 32) {
    throw new Error(`Invalid private key length: ${privateKey.length} (expected 32)`);
  }

  // Build PKCS#8 structure for P-256 private key
  const pkcs8 = buildPkcs8PrivateKey(privateKey);
  console.log('[P256] importPrivateKey: PKCS#8 length =', pkcs8.length);

  try {
    const key = await crypto.subtle.importKey(
      'pkcs8',
      pkcs8,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false, // Non-extractable after import
      ['sign']
    );
    console.log('[P256] importPrivateKey: SUCCESS');
    return key;
  } catch (e: any) {
    console.error('[P256] importPrivateKey FAILED:', e.message, e);
    // Try alternative JWK import method
    console.log('[P256] Trying JWK import fallback...');
    return importPrivateKeyAsJwk(privateKey);
  }
}

/**
 * Alternative import method using JWK format.
 * Some browsers handle JWK better than PKCS#8.
 */
async function importPrivateKeyAsJwk(privateKey: Uint8Array): Promise<CryptoKey> {
  // We need to derive the public key from the private key for JWK
  // For now, generate a temporary keypair to get a template
  const tempKeyPair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify']
  );

  // Export as JWK to get the structure
  const tempJwk = await crypto.subtle.exportKey('jwk', tempKeyPair.privateKey);

  // Replace 'd' (private key) with our private key
  // JWK uses base64url encoding
  const dBase64url = arrayToBase64Url(privateKey);

  // We need to compute x and y from d for this to work properly
  // This is complex without the public key. Let's try a different approach.
  console.log('[P256] JWK fallback not fully implemented, trying raw import...');
  throw new Error('PKCS#8 import failed and JWK fallback not available');
}

/**
 * Sign data with a P-256 private key.
 *
 * @param data - Data to sign
 * @param privateKey - Raw 32-byte private key
 * @param publicKey - Optional public key (x, y) for JWK import fallback
 * @returns IEEE P1363 signature (64 bytes)
 */
export async function signWithP256(
  data: Uint8Array,
  privateKey: Uint8Array,
  publicKey?: P256PublicKey
): Promise<Uint8Array> {
  console.log('[P256] signWithP256: data length =', data.length, 'privateKey length =', privateKey.length);

  let cryptoKey: CryptoKey;

  try {
    cryptoKey = await importPrivateKey(privateKey);
  } catch (pkcs8Error: any) {
    console.warn('[P256] PKCS#8 import failed:', pkcs8Error.message);

    // If we have public key, try JWK import (more reliable)
    if (publicKey) {
      console.log('[P256] Trying JWK import with public key...');
      cryptoKey = await importPrivateKeyWithPublicKey(privateKey, publicKey);
    } else {
      throw pkcs8Error;
    }
  }

  console.log('[P256] Signing data...');
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    cryptoKey,
    data
  );

  console.log('[P256] Signature generated, length =', signature.byteLength);

  // Web Crypto returns IEEE P1363 format (r || s, 64 bytes)
  return new Uint8Array(signature);
}

/**
 * Import private key using JWK format with public key coordinates.
 * This is more reliable than PKCS#8 across different browsers.
 */
async function importPrivateKeyWithPublicKey(
  privateKey: Uint8Array,
  publicKey: P256PublicKey
): Promise<CryptoKey> {
  // Decode public key coordinates from base64
  const xBytes = base64ToArray(publicKey.x);
  const yBytes = base64ToArray(publicKey.y);

  console.log('[P256] JWK import: x length =', xBytes.length, 'y length =', yBytes.length);

  // Build JWK
  const jwk: JsonWebKey = {
    kty: 'EC',
    crv: 'P-256',
    x: arrayToBase64Url(xBytes),
    y: arrayToBase64Url(yBytes),
    d: arrayToBase64Url(privateKey),
  };

  console.log('[P256] Importing JWK...');
  const key = await crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign']
  );

  console.log('[P256] JWK import SUCCESS');
  return key;
}

// --- Helper functions ---

/**
 * Extract raw 32-byte private key from PKCS#8 format.
 * P-256 PKCS#8 structure has the private key at a known offset.
 */
function extractRawPrivateKey(pkcs8: Uint8Array): Uint8Array {
  // PKCS#8 for P-256 has the 32-byte private key starting at offset 36
  // This is after: sequence, version, algorithm identifier, octet string header
  // The exact structure:
  // SEQUENCE {
  //   INTEGER 0 (version)
  //   SEQUENCE { OID 1.2.840.10045.2.1, OID 1.2.840.10045.3.1.7 }
  //   OCTET STRING { SEQUENCE { INTEGER 1, OCTET STRING[32] <private key>, ... } }
  // }

  // Find the private key octet string
  // It's wrapped in another SEQUENCE inside the outer OCTET STRING
  // The 32-byte key is after: 0x04 0x20 (octet string, length 32)
  for (let i = 0; i < pkcs8.length - 32; i++) {
    if (pkcs8[i] === 0x04 && pkcs8[i + 1] === 0x20) {
      return pkcs8.slice(i + 2, i + 34);
    }
  }

  throw new Error('Failed to extract private key from PKCS#8');
}

/**
 * Extract x,y coordinates from SPKI public key format.
 * P-256 SPKI has the uncompressed point (04 || x || y) at a known location.
 */
function extractPublicKeyCoordinates(spki: Uint8Array): { x: Uint8Array; y: Uint8Array } {
  // SPKI for P-256 contains the uncompressed point at the end
  // Format: 0x04 || x[32] || y[32]
  // Total point size is 65 bytes

  // Find the uncompressed point marker (0x04) followed by 64 bytes
  for (let i = 0; i < spki.length - 64; i++) {
    if (spki[i] === 0x04 && spki.length - i >= 65) {
      return {
        x: spki.slice(i + 1, i + 33),
        y: spki.slice(i + 33, i + 65),
      };
    }
  }

  throw new Error('Failed to extract public key coordinates from SPKI');
}

/**
 * Build PKCS#8 structure from raw 32-byte P-256 private key.
 */
function buildPkcs8PrivateKey(privateKey: Uint8Array): Uint8Array {
  if (privateKey.length !== 32) {
    throw new Error('P-256 private key must be 32 bytes');
  }

  // PKCS#8 header for P-256 (before the private key)
  // This includes: SEQUENCE, version, algorithm identifier
  const header = new Uint8Array([
    0x30, 0x81, 0x87, // SEQUENCE, length 135
    0x02, 0x01, 0x00, // INTEGER 0 (version)
    0x30, 0x13, // SEQUENCE (algorithm identifier)
    0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, // OID 1.2.840.10045.2.1 (ecPublicKey)
    0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, // OID 1.2.840.10045.3.1.7 (P-256)
    0x04, 0x6d, // OCTET STRING, length 109
    0x30, 0x6b, // SEQUENCE, length 107
    0x02, 0x01, 0x01, // INTEGER 1 (EC private key version)
    0x04, 0x20, // OCTET STRING, length 32 (private key follows)
  ]);

  // Suffix after private key (public key point, optional but required by some implementations)
  const suffix = new Uint8Array([
    0xa1, 0x44, // [1] EXPLICIT, length 68
    0x03, 0x42, 0x00, // BIT STRING, length 66, unused bits 0
    0x04, // Uncompressed point marker
    // Public key x,y would go here (64 bytes), but we'll leave it empty
    // Some implementations don't require the public key in PKCS#8
  ]);

  // For import, we need a minimal structure that works
  // Web Crypto can derive the public key from the private key
  const minimalPkcs8 = new Uint8Array([
    0x30, 0x41, // SEQUENCE, length 65
    0x02, 0x01, 0x00, // INTEGER 0 (version)
    0x30, 0x13, // SEQUENCE (algorithm identifier)
    0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, // OID ecPublicKey
    0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, // OID P-256
    0x04, 0x27, // OCTET STRING, length 39
    0x30, 0x25, // SEQUENCE, length 37
    0x02, 0x01, 0x01, // INTEGER 1
    0x04, 0x20, // OCTET STRING, length 32
    ...privateKey, // 32-byte private key
  ]);

  return minimalPkcs8;
}

/**
 * Convert Uint8Array to base64 string.
 */
function arrayToBase64(array: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < array.length; i++) {
    binary += String.fromCharCode(array[i]);
  }
  return btoa(binary);
}

/**
 * Convert Uint8Array to base64url string (URL-safe, no padding).
 */
function arrayToBase64Url(array: Uint8Array): string {
  const base64 = arrayToBase64(array);
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Convert base64 or base64url string to Uint8Array.
 * Handles both standard base64 (+/) and base64url (-_) formats.
 */
export function base64ToArray(input: string): Uint8Array {
  // Convert base64url to standard base64
  let base64 = input
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  // Add padding if needed
  const padLength = (4 - (base64.length % 4)) % 4;
  base64 += '='.repeat(padLength);

  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return array;
}
